/**
 * React 版本的格式化工具
 * 无 Vue 依赖，纯函数实现
 */

// 格式化选项接口
export interface FormatOptions {
  [key: string]: any;
  currency?: string;
  precision?: number;
  notation?: "compact" | "scientific" | "engineering";
  pattern?: string;
  binary?: boolean;
  country?: string;
  mask?: boolean;
  separator?: string;
  maxLength?: number;
  suffix?: string;
  statusMap?: Record<string, string>;
  delimiter?: string;
}

// 格式化类型
export type FormatType =
  | "currency"
  | "number"
  | "percent"
  | "compact"
  | "scientific"
  | "ordinal"
  | "chineseNumber"
  | "timestamp"
  | "date"
  | "dateCustom"
  | "relativeTime"
  | "duration"
  | "age"
  | "fileSize"
  | "networkSpeed"
  | "phone"
  | "phoneMask"
  | "landline"
  | "emailMask"
  | "idCard"
  | "bankCard"
  | "ipAddress"
  | "macAddress"
  | "truncate"
  | "capitalize"
  | "titleCase"
  | "kebabCase"
  | "camelCase"
  | "snakeToCamel"
  | "camelToSnake"
  | "stripHtml"
  | "usernameMask"
  | "status"
  | "gender"
  | "url"
  | "color"
  | "array";

// 工具函数：安全的数字转换
const toNumber = (value: any): number => {
  if (value === null || value === undefined || value === "") return 0;
  const num = Number(value);
  return isNaN(num) || !isFinite(num) ? 0 : num;
};

// 工具函数：安全的字符串转换
const toString = (value: any): string => {
  if (value === null || value === undefined) return "";
  return String(value);
};

// 工具函数：安全的日期转换
const toDate = (value: any): Date | null => {
  if (value === null || value === undefined) return null;

  let date: Date;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === "number") {
    const timestamp = value.toString().length === 10 ? value * 1000 : value;
    if (timestamp < 0 || timestamp > 8640000000000000) return null;
    date = new Date(timestamp);
  } else {
    date = new Date(value);
  }

  return isNaN(date.getTime()) ? null : date;
};

// 货币格式化
export const formatCurrency = (
  value: any,
  currency = "CNY",
  options: FormatOptions = {}
): string => {
  try {
    const num = toNumber(value);
    const safeCurrency = /^[A-Z]{3}$/.test(currency) ? currency : "CNY";
    return new Intl.NumberFormat("zh-CN", {
      style: "currency",
      currency: safeCurrency,
      ...options,
    }).format(num);
  } catch {
    return "";
  }
};

// 数字格式化
export const formatNumber = (
  value: any,
  options: FormatOptions = {}
): string => {
  try {
    const num = toNumber(value);
    return new Intl.NumberFormat("zh-CN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      ...options,
    }).format(num);
  } catch {
    return "";
  }
};

// 百分比格式化
export const formatPercent = (value: any, precision = 2): string => {
  try {
    const num = toNumber(value);
    const safePrecision = Math.max(0, Math.min(20, Math.floor(precision)));
    return new Intl.NumberFormat("zh-CN", {
      style: "percent",
      minimumFractionDigits: safePrecision,
      maximumFractionDigits: safePrecision,
    }).format(num);
  } catch {
    return "";
  }
};

// 紧凑数字格式化
export const formatCompact = (
  value: any,
  notation: "compact" | "scientific" | "engineering" = "compact"
): string => {
  try {
    const num = toNumber(value);
    return new Intl.NumberFormat("zh-CN", {
      notation,
      compactDisplay: "short",
    }).format(num);
  } catch {
    return "";
  }
};

// 日期格式化
export const formatDate = (value: any, options: FormatOptions = {}): string => {
  try {
    const date = toDate(value);
    if (!date) return "";
    return new Intl.DateTimeFormat("zh-CN", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      ...options,
    }).format(date);
  } catch {
    return "";
  }
};

// 自定义日期格式化
export const formatDateCustom = (
  value: any,
  pattern = "YYYY-MM-DD"
): string => {
  try {
    const date = toDate(value);
    if (!date) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");

    return pattern
      .replace("YYYY", String(year))
      .replace("MM", month)
      .replace("DD", day)
      .replace("HH", hours)
      .replace("mm", minutes)
      .replace("ss", seconds);
  } catch {
    return "";
  }
};

// 时间戳格式化
export const formatTimestamp = (
  value: any,
  pattern = "YYYY-MM-DD HH:mm:ss"
): string => {
  return formatDateCustom(value, pattern);
};

// 相对时间
export const formatRelativeTime = (value: any): string => {
  try {
    const date = toDate(value);
    if (!date) return "";

    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (seconds < 60) return "刚刚";
    if (minutes < 60) return `${minutes}分钟前`;
    if (hours < 24) return `${hours}小时前`;
    if (days < 30) return `${days}天前`;
    if (months < 12) return `${months}个月前`;
    return `${years}年前`;
  } catch {
    return "";
  }
};

// 时间段格式化
export const formatDuration = (seconds: any): string => {
  try {
    const value = Math.floor(toNumber(seconds));
    if (value < 0 || value > 31536000) return "";

    const hours = Math.floor(value / 3600);
    const minutes = Math.floor((value % 3600) / 60);
    const secs = value % 60;

    const parts: string[] = [];
    if (hours > 0) parts.push(`${hours}小时`);
    if (minutes > 0) parts.push(`${minutes}分钟`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}秒`);

    return parts.join("");
  } catch {
    return "";
  }
};

// 年龄计算
export const formatAge = (birthDate: any): string => {
  try {
    const birth = toDate(birthDate);
    if (!birth) return "";

    const today = new Date();
    if (birth > today) return "";

    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }

    if (age < 0 || age > 150) return "";
    return `${age}岁`;
  } catch {
    return "";
  }
};

// 文件大小格式化
export const formatFileSize = (
  bytes: any,
  precision = 2,
  binary = true
): string => {
  try {
    const value = toNumber(bytes);
    if (value < 0 || value > Number.MAX_SAFE_INTEGER) return "";
    if (value === 0) return "0 B";

    const safePrecision = Math.max(0, Math.min(10, Math.floor(precision)));
    const k = binary ? 1024 : 1000;
    const sizes = binary
      ? ["B", "KiB", "MiB", "GiB", "TiB", "PiB"]
      : ["B", "KB", "MB", "GB", "TB", "PB"];
    const i = Math.min(
      Math.floor(Math.log(value) / Math.log(k)),
      sizes.length - 1
    );

    return `${parseFloat((value / Math.pow(k, i)).toFixed(safePrecision))} ${
      sizes[i]
    }`;
  } catch {
    return "";
  }
};

// 手机号格式化
export const formatPhone = (phone: any, country = "CN"): string => {
  try {
    const value = toString(phone).replace(/\D/g, "");
    if (!value) return "";

    if (
      country === "CN" &&
      value.length === 11 &&
      /^1[3-9]\d{9}$/.test(value)
    ) {
      return value.replace(/(\d{3})(\d{4})(\d{4})/, "$1 $2 $3");
    }
    if (country === "US" && value.length === 10) {
      return value.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
    }
    return toString(phone);
  } catch {
    return "";
  }
};

// 手机号脱敏
export const formatPhoneMask = (phone: any): string => {
  try {
    const value = toString(phone).replace(/\D/g, "");
    if (value.length === 11) {
      return value.replace(/(\d{3})\d{4}(\d{4})/, "$1****$2");
    }
    return toString(phone);
  } catch {
    return "";
  }
};

// 邮箱脱敏
export const formatEmailMask = (email: any): string => {
  try {
    const value = toString(email);
    if (!value || !value.includes("@")) return "";

    const [username, domain] = value.split("@");
    if (username.length <= 1) return value;
    if (username.length <= 3) {
      return `${username[0]}*@${domain}`;
    }

    const maskedUsername =
      username[0] +
      "*".repeat(username.length - 2) +
      username[username.length - 1];
    return `${maskedUsername}@${domain}`;
  } catch {
    return "";
  }
};

// 身份证号脱敏
export const formatIdCard = (idCard: any, mask = true): string => {
  try {
    const value = toString(idCard).replace(/\s/g, "");

    if (value.length === 18 && /^\d{17}[\dXx]$/.test(value)) {
      if (mask) {
        return value.replace(/(\d{6})\d{8}(\d{3}[\dXx])/, "$1********$2");
      }
      return value.replace(/(\d{6})(\d{8})(\d{4})/, "$1 $2 $3");
    }
    return "";
  } catch {
    return "";
  }
};

// 银行卡号格式化
export const formatBankCard = (cardNumber: any, mask = true): string => {
  try {
    const value = toString(cardNumber).replace(/\s/g, "");
    if (!/^\d{13,19}$/.test(value)) return "";

    if (mask && value.length >= 8) {
      const start = value.slice(0, 4);
      const end = value.slice(-4);
      const middleLength = value.length - 8;
      const middle = "*".repeat(middleLength);

      const fullMasked = start + middle + end;
      return fullMasked.replace(/(.{4})/g, "$1 ").trim();
    }

    return value.replace(/(.{4})/g, "$1 ").trim();
  } catch {
    return "";
  }
};

// 用户名脱敏
export const formatUsernameMask = (username: any): string => {
  try {
    const value = toString(username);
    if (value.length <= 1) return value;
    if (value.length === 2) return value[0] + "*";
    return value[0] + "*".repeat(value.length - 2) + value[value.length - 1];
  } catch {
    return "";
  }
};

// 文本截断
export const formatTruncate = (
  text: any,
  maxLength = 50,
  suffix = "..."
): string => {
  try {
    const value = toString(text);
    const safeMaxLength = Math.max(1, Math.min(1000, Math.floor(maxLength)));

    if (value.length <= safeMaxLength) return value;
    return value.slice(0, safeMaxLength) + suffix;
  } catch {
    return "";
  }
};

// 首字母大写
export const formatCapitalize = (text: any): string => {
  try {
    const value = toString(text);
    if (!value) return "";
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  } catch {
    return "";
  }
};

// 数组格式化
export const formatArray = (value: any, delimiter = ", "): string => {
  try {
    if (Array.isArray(value)) {
      return value.join(delimiter);
    }
    return toString(value);
  } catch {
    return "";
  }
};

// 性别格式化
export const formatGender = (value: any): string => {
  const genderMap: Record<string, string> = {
    "0": "女",
    "1": "男",
    "2": "未知",
    male: "男",
    female: "女",
    m: "男",
    f: "女",
    M: "男",
    F: "女",
  };
  return genderMap[toString(value)] || toString(value);
};

// 状态格式化
export const formatStatus = (
  value: any,
  statusMap?: Record<string, string>
): string => {
  if (!statusMap) {
    const defaultMap: Record<string, string> = {
      "0": "禁用",
      "1": "启用",
      true: "是",
      false: "否",
    };
    return defaultMap[toString(value)] || toString(value);
  }
  return statusMap[toString(value)] || toString(value);
};

// 移除 HTML 标签
export const formatStripHtml = (value: any): string => {
  try {
    return toString(value).replace(/<[^>]*>/g, "");
  } catch {
    return "";
  }
};

/**
 * 根据类型格式化值
 */
export function formatByType(
  value: any,
  formatType: FormatType | string,
  options: FormatOptions = {}
): string {
  if (!formatType || value === undefined || value === null) {
    return toString(value);
  }

  switch (formatType) {
    case "currency":
      return formatCurrency(value, options.currency, options);
    case "number":
      return formatNumber(value, options);
    case "percent":
      return formatPercent(value, options.precision);
    case "compact":
      return formatCompact(value, options.notation);
    case "timestamp":
      return formatTimestamp(value, options.pattern);
    case "date":
      return formatDate(value, options);
    case "dateCustom":
      return formatDateCustom(value, options.pattern);
    case "relativeTime":
      return formatRelativeTime(value);
    case "duration":
      return formatDuration(value);
    case "age":
      return formatAge(value);
    case "fileSize":
      return formatFileSize(value, options.precision, options.binary);
    case "phone":
      return formatPhone(value, options.country);
    case "phoneMask":
      return formatPhoneMask(value);
    case "emailMask":
      return formatEmailMask(value);
    case "idCard":
      return formatIdCard(value, options.mask);
    case "bankCard":
      return formatBankCard(value, options.mask);
    case "usernameMask":
      return formatUsernameMask(value);
    case "truncate":
      return formatTruncate(value, options.maxLength, options.suffix);
    case "capitalize":
      return formatCapitalize(value);
    case "array":
      return formatArray(value, options.delimiter);
    case "gender":
      return formatGender(value);
    case "status":
      return formatStatus(value, options.statusMap);
    case "stripHtml":
      return formatStripHtml(value);
    default:
      return toString(value);
  }
}

export default formatByType;
